import type { ReactNode } from "react";
import type { ElementCategory } from "../model/inp/properties.ts";
import { kindLabelKey } from "../i18n/fieldLabels.ts";
import { useT } from "../i18n/hooks.ts";
import { SelectionToolsIcon } from "./SelectionToolsIcon.tsx";
import { SelectRectangleIcon } from "./SelectRectangleIcon.tsx";
import { SelectLassoIcon } from "./SelectLassoIcon.tsx";
import { AddJunctionIcon } from "./AddJunctionIcon.tsx";
import { AddOutfallIcon } from "./AddOutfallIcon.tsx";
import { AddStorageIcon } from "./AddStorageIcon.tsx";
import { AddDividerIcon } from "./AddDividerIcon.tsx";
import { AddRaingageIcon } from "./AddRaingageIcon.tsx";
import { AddConduitIcon } from "./AddConduitIcon.tsx";
import { AddPumpIcon } from "./AddPumpIcon.tsx";
import { AddOrificeIcon } from "./AddOrificeIcon.tsx";
import { AddWeirIcon } from "./AddWeirIcon.tsx";
import { AddOutletIcon } from "./AddOutletIcon.tsx";
import { AddSubcatchmentIcon } from "./AddSubcatchmentIcon.tsx";
import { MapQueryIcon } from "./MapQueryIcon.tsx";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu.tsx";

type ShapeTool = "select-rect" | "select-lasso";

interface KindRow {
  category: ElementCategory;
  kind: string;
  icon: ReactNode;
}

const NODE_ROWS: KindRow[] = [
  { category: "node", kind: "junction", icon: <AddJunctionIcon /> },
  { category: "node", kind: "outfall", icon: <AddOutfallIcon /> },
  { category: "node", kind: "storage", icon: <AddStorageIcon /> },
  { category: "node", kind: "divider", icon: <AddDividerIcon /> },
];
const LINK_ROWS: KindRow[] = [
  { category: "link", kind: "conduit", icon: <AddConduitIcon /> },
  { category: "link", kind: "pump", icon: <AddPumpIcon /> },
  { category: "link", kind: "orifice", icon: <AddOrificeIcon /> },
  { category: "link", kind: "weir", icon: <AddWeirIcon /> },
  { category: "link", kind: "outlet", icon: <AddOutletIcon /> },
];
const OTHER_ROWS: KindRow[] = [
  { category: "raingage", kind: "raingage", icon: <AddRaingageIcon /> },
  { category: "subcatchment", kind: "subcatchment", icon: <AddSubcatchmentIcon /> },
];

/**
 * The "Selección" toolbar menu — everything beyond a plain click/shift-click:
 * rectangle/lasso drag-select (each just arms a mode; App.tsx does the
 * rest), select-by-type (selects every junction/conduit/etc. of one kind at
 * once), and select-by-query (opens the existing Map Query bar).
 */
export function SelectionToolsMenu({
  mode,
  onSelectShapeTool,
  onSelectByType,
  onOpenQuerySelect,
  open,
  onToggle,
  onClose,
}: {
  mode: string;
  onSelectShapeTool: (tool: ShapeTool) => void;
  onSelectByType: (category: ElementCategory, kind: string) => void;
  onOpenQuerySelect: () => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const t = useT();

  const isShapeActive = mode === "select-rect" || mode === "select-lasso";

  function kindButton(row: KindRow) {
    const labelKey = kindLabelKey(row.kind);
    return (
      <DropdownMenuItem
        key={`${row.category}-${row.kind}`}
        icon={row.icon}
        label={labelKey ? t(labelKey) : row.kind}
        onClick={() => {
          onClose();
          onSelectByType(row.category, row.kind);
        }}
      />
    );
  }

  return (
    <DropdownMenu
      open={open}
      onToggle={onToggle}
      onClose={onClose}
      // UI redesign phase 3/4 — this trigger now lives in ToolRail.tsx's
      // vertical strip, docked to the LEFT edge of the viewport, not the
      // right-hand end of a horizontal topbar `align="right"` (the
      // default) assumed. Aligning the popover's own left edge to the
      // trigger's left edge instead opens it rightward, into the map area
      // — `align="right"` here pushed it almost entirely off-screen to
      // the left (found live).
      align="left"
      triggerClassName={
        isShapeActive
          ? "tool-rail-button tool-rail-button-active dropdown-trigger"
          : "tool-rail-button dropdown-trigger"
      }
      triggerLabel={t("toolbar.selectionTools")}
      triggerIcon={<SelectionToolsIcon />}
    >
      <DropdownMenuItem
        icon={<SelectRectangleIcon />}
        label={t("selectionTools.rectangle")}
        active={mode === "select-rect"}
        onClick={() => {
          onClose();
          onSelectShapeTool("select-rect");
        }}
      />
      <DropdownMenuItem
        icon={<SelectLassoIcon />}
        label={t("selectionTools.lasso")}
        active={mode === "select-lasso"}
        onClick={() => {
          onClose();
          onSelectShapeTool("select-lasso");
        }}
      />
      <span className="menu-popover-group-label">{t("selectionTools.byType")}</span>
      {NODE_ROWS.map(kindButton)}
      {LINK_ROWS.map(kindButton)}
      {OTHER_ROWS.map(kindButton)}
      <DropdownMenuItem
        icon={<MapQueryIcon />}
        label={t("selectionTools.byQuery")}
        onClick={() => {
          onClose();
          onOpenQuerySelect();
        }}
      />
    </DropdownMenu>
  );
}
